import { useRef, useState } from "react";
import { FileUp, FileText, X } from "lucide-react";
import { UI, useLang } from "../../lib/lang";

const ALLOWED = ["pdf", "jpg", "jpeg", "png", "docx"];
const MAX = 10 * 1024 * 1024;
const MAX_FILES = 5;

const fmt = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export const FileDrop = ({ files, setFiles, error, setError }) => {
  const input = useRef(null);
  const [drag, setDrag] = useState(false);
  const t = UI[useLang()];

  const add = (list) => {
    const next = [...files];
    const problems = [];
    Array.from(list).forEach((f) => {
      const ext = f.name.split(".").pop().toLowerCase();
      if (!ALLOWED.includes(ext)) problems.push(t.typeErr(f.name));
      else if (f.size > MAX) problems.push(t.sizeErr(f.name));
      else if (next.length >= MAX_FILES) problems.push(t.countErr);
      else next.push(f);
    });
    setFiles(next);
    setError(problems.length ? problems.join(" ") : undefined);
  };

  return (
    <div>
      <p id="files-label" className="mb-2 block text-sm font-semibold text-[color:var(--navy)]">{t.filesLabel} <span className="font-normal text-slate-500">(optional)</span></p>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); add(e.dataTransfer.files); }}
        className={`rounded-2xl border-2 border-dashed p-6 md:p-8 text-center transition-colors ${drag ? "border-[color:var(--teal)] bg-[color:var(--mist)]" : error ? "border-[#B91C1C]" : "border-slate-300"}`}
        data-testid="file-dropzone"
      >
        <FileUp size={28} className="mx-auto text-[color:var(--teal-ink)]" />
        <p className="mt-3 text-sm text-slate-700">{t.drop}</p>
        <button type="button" onClick={() => input.current?.click()} aria-describedby="files-label files-hint" data-testid="file-select-button"
          className="mt-3 rounded-full border border-[color:var(--navy)] px-5 py-2.5 text-sm font-semibold text-[color:var(--navy)] hover:bg-[color:var(--mist)]">
          {t.choose}
        </button>
        <input ref={input} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.docx" className="hidden" data-testid="file-input"
          onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
        <p id="files-hint" className="mt-3 text-xs text-slate-500">{t.filesHint}</p>
      </div>
      {error && <p role="alert" className="mt-2 text-sm text-[#B91C1C]" data-testid="error-files">{error}</p>}
      {files.length > 0 && (
        <ul className="mt-4 space-y-2" data-testid="file-list">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm">
              <span className="flex min-w-0 items-center gap-2"><FileText size={16} className="shrink-0 text-[color:var(--teal-ink)]" /><span className="truncate">{f.name}</span><span className="shrink-0 text-slate-500">{fmt(f.size)}</span></span>
              <button type="button" aria-label={t.remove(f.name)} onClick={() => { setFiles(files.filter((_, j) => j !== i)); setError(undefined); }} className="rounded-full p-1.5 hover:bg-slate-100" data-testid={`file-remove-${i}`}>
                <X size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
